import { createGlobalStyle } from 'styled-components';

import { barclaysPremierLeagueSrc } from './fonts/barclays-premier-league';
import { bebasNeueSrc } from './fonts/bebas-neue/bebas-neueSrc';
import { craftSansSrc } from './fonts/craft-sans/craftSansSrc';
import { exoSrc } from './fonts/exo';
import { futuraSrc } from './fonts/futura/futuraSrc';
import { gothamSrc } from './fonts/gotham';
import { helveticaSrc } from './fonts/helvetica/helveticaNeueSrc';
import { interSrc } from './fonts/inter/interSrc';
import { libreFranklinSrc } from './fonts/libre-franklin/libreFranklinSrc';
import { loraSrc } from './fonts/lora/loraSrc';
import { mulishSrc } from './fonts/mulish/mulishSrc';
import { recifeDisplaySrc } from './fonts/recife-display/recifeDisplaySrc';
import { robotoSrc } from './fonts/roboto/robotoSrc';
import { valueSansSrc } from './fonts/value-sans/valueSansSrc';

export const FontStyle = createGlobalStyle`

  body {
    font-size: ${({ theme }) => theme.typography.fontSize};
    font-family: ${({ theme }) => theme.typography.f1400family}, helvetica, sans-serif;
  }
  h1, h2, h3 {
    font-family: ${({ theme }) => theme.typography.f2700family}, helvetica, sans-serif;
    font-weight: 700;
  }
  h3 {
    font-family: ${({ theme }) => theme.typography.f1700family}, helvetica, sans-serif;
    font-weight: 700;
  }
  h4 {
    font-family: ${({ theme }) => theme.typography.f1500family}, helvetica, sans-serif;
    font-weight: 500;
  }
  .f1-400 {
    font-family: ${({ theme }) => theme.typography.f1400family}, helvetica, sans-serif;
    font-weight: 400;
  }
  .f1-500 {
    font-family: ${({ theme }) => theme.typography.f1500family}, helvetica, sans-serif;
    font-weight: 500;
  }
  .f1-700 {
    font-family: ${({ theme }) => theme.typography.f1700family}, helvetica, sans-serif;
    font-weight: 700;
  }
  .f2-400 {
    font-family: ${({ theme }) => theme.typography.f2400family}, helvetica, sans-serif;
    font-weight: 400;
  }
  .f2-700 {
    font-family: ${({ theme }) => theme.typography.f2700family}, helvetica, sans-serif;
    font-weight: 700;
  }
  .f3-300 {
    font-family: ${({ theme }) => theme.typography.f3300family}, helvetica, sans-serif;
    font-weight: 300;
  }
  .f3-400 {
    font-family: ${({ theme }) => theme.typography.f3400family}, helvetica, sans-serif;
    font-weight: 400;
  }
  .f3-500 {
    font-family: ${({ theme }) => theme.typography.f3500family}, helvetica, sans-serif;
    font-weight: 500;
  }
  .f3-700 {
    font-family: ${({ theme }) => theme.typography.f3700family}, helvetica, sans-serif;
    font-weight: 700;
  }
`;

export type Font =
    | 'Antenna Ex Cond'
    | 'Antenna'
    | 'Bebas Neue'
    | 'Craft Sans'
    | 'Exo'
    | 'Futura'
    | 'Helvetica'
    | 'Inter'
    | 'Libre Franklin'
    | 'Lora'
    | 'Mulish'
    | 'Premier League'
    | 'Recife Display'
    | 'Roboto'
    | 'Value Sans'
    | 'Gotham'
    | 'urw-din-semi-condensed'
    | 'urw-form';

export function getFontLink(key?: string) {
    switch (key) {
        case 'Antenna Ex Cond':
        case 'Antenna':
            return (
                <link
                    href="//cloud.typenetwork.com/projects/5402/fontface.css/"
                    rel="stylesheet"
                    type="text/css"
                ></link>
            );
        case 'urw-din-semi-condensed':
            return <link href="https://use.typekit.net/ejk4rne.css" rel="stylesheet" type="text/css"></link>;
        case 'Inter':
            return '';
        case 'Roboto':
            return '';
        case 'Libre Franklin':
            return '';
        case 'Craft Sans':
            return '';
        case 'Exo':
            return '';
        case 'Recife Display':
            return '';
        case 'Value Sans':
            return '';
        case 'Helvetica':
            return '';
        case 'urw-form':
        default:
            return <link href="https://use.typekit.net/obe0xuy.css" rel="stylesheet" type="text/css"></link>;
    }
}

export function getLocalFontSrc(key: Font) {
    const def = interSrc;
    switch (key) {
        case 'Inter':
            return def;
        case 'Craft Sans':
            return craftSansSrc;
        case 'Exo':
            return exoSrc;
        case 'Roboto':
            return robotoSrc;
        case 'Libre Franklin':
            return libreFranklinSrc;
        case 'Recife Display':
            return recifeDisplaySrc;
        case 'Value Sans':
            return valueSansSrc;
        case 'Helvetica':
            return helveticaSrc;
        case 'Bebas Neue':
            return bebasNeueSrc;
        case 'Premier League':
            return barclaysPremierLeagueSrc;
        case 'Gotham':
            return gothamSrc;
        case 'Mulish':
            return mulishSrc;
        case 'Lora':
            return loraSrc;
        case 'Futura':
            return futuraSrc;
        case 'Antenna':
        case 'Antenna Ex Cond':
        case 'urw-din-semi-condensed':
        case 'urw-form':
        default:
            return def;
    }
}

export function getFontFamily(key: Font) {
    switch (key) {
        case 'Antenna':
            return {
                400: 'Antenna Reg',
                500: 'Antenna Med',
                700: 'Antenna Bd',
            };
        case 'Antenna Ex Cond':
            return {
                400: 'Antenna Ex Cond Med',
                500: 'Antenna Ex Cond Med',
                700: 'Antenna Ex Cond Med',
            };
        case 'Inter':
            return {
                400: 'Inter',
                500: 'Inter',
                700: 'Inter',
            };
        case 'Craft Sans':
            return {
                400: 'Craft Sans',
                500: 'Craft Sans',
                700: 'Craft Sans',
            };
        case 'Exo':
            return {
                400: 'Exo',
                500: 'Exo',
                700: 'Exo',
            };
        case 'urw-din-semi-condensed':
            return {
                400: 'urw-din-semi-condensed',
                500: 'urw-din-semi-condensed',
                700: 'urw-din-semi-condensed',
            };
        case 'Roboto':
            return {
                400: 'Roboto',
                500: 'Roboto',
                700: 'Roboto',
            };
        case 'Premier League':
            return {
                400: 'Premier League',
                500: 'Premier League',
                700: 'Premier League',
            };
        case 'Libre Franklin':
            return {
                400: 'Libre Franklin',
                500: 'Libre Franklin',
                700: 'Libre Franklin',
            };
        case 'Recife Display':
            return {
                400: 'Recife Display',
                500: 'Recife Display',
                700: 'Recife Display',
            };
        case 'Value Sans':
            return {
                400: 'Value Sans',
                500: 'Value Sans',
                700: 'Value Sans',
            };
        case 'Helvetica':
            return {
                400: 'Helvetica',
                500: 'Helvetica',
                700: 'Helvetica',
            };
        case 'Bebas Neue':
            return {
                400: 'Bebas Neue',
                500: 'Bebas Neue',
                700: 'Bebas Neue',
            };
        case 'Gotham':
            return {
                400: 'Gotham',
                500: 'Gotham',
                700: 'Gotham',
            };
        case 'Mulish':
            return {
                400: 'Mulish',
                500: 'Mulish',
                700: 'Mulish',
            };
        case 'Lora':
            return {
                400: 'Lora',
                500: 'Lora',
                700: 'Lora',
            };
        case 'Futura':
            return {
                400: 'Futura',
                500: 'Futura',
                700: 'Futura',
            };
        case 'urw-form':
        default:
            return {
                400: 'urw-form',
                500: 'urw-form',
                700: 'urw-form',
            };
    }
}

// export function loadFontForPDF(font: FontStore, family: Font) {
//     font.clear();
//     // Register global PDF Font
//     const currentFont = getLocalFontSrc(family);
//     font.register({
//         family: 'PDF Font',
//         src: currentFont[400].ttf ?? currentFont[400].otf,
//     });

//     font.register({
//         family: 'PDF Font SemiBold',
//         src: currentFont[500].ttf ?? currentFont[500].otf,
//     });

//     font.register({
//         family: 'PDF Font Bold',
//         src: currentFont[700].ttf ?? currentFont[700].otf,
//     });
// }

export default FontStyle;
